import { useAppController } from 'customHooks/useAppController'
import { BomItemMaterialDiscountPercentageRef } from 'features/BillOfMaterials/components/BomItemMaterialDiscountPercentage'
import { BomItemMaterialSurchargePercentageRef } from 'features/BillOfMaterials/components/BomItemMaterialSurchargePercentage'
import { saveBomItemIsPurchasingPart } from 'features/BillOfMaterials/store/asyncActions/saveBomItemIsPurchasingPart'
import { bomItemActivitiesSelectors } from 'features/BillOfMaterials/store/selectors/activities/bomItemActivitiesSelectors'
import { ProjectEditableStateSelector } from 'features/BillOfMaterials/store/selectors/ProjectEditableStateSelector'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  KeywordDto,
  MaterialSummaryDto,
  MoneyDto,
  QuantityDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import { MaterialActions } from 'store/MaterialSelector/MaterialAsyncActions'
import { materialActions } from 'store/MaterialSelector/MaterialsReducer'
import { PurchasingInfoController } from './PurchasingInfoController'

export type CustomPrice = {
  CostPrice: MoneyDto
  SurchargeRatio: QuantityDto
  DiscountRatio: QuantityDto
}

export const usePurchasingPartDialog = (bomItemPointer: BomItemPointer) => {
  const dispatch = useAppDispatch()

  const projectIsEditable = useAppSelector(ProjectEditableStateSelector)

  const purchasingActivity = useAppSelector(
    bomItemActivitiesSelectors.byWorkingStep(
      bomItemPointer,
      WorkingStepType.Purchasing,
    ),
    isEqual,
  )

  const currentDefinedCostPrice = useAppSelector(
    projectSelectors.bomItemMaterialFinancialSelector(
      bomItemPointer,
      purchasingActivity?.id,
      'costPricePerItem',
    ),
    isEqual
  ) as MoneyDto

  const currentDefinedSurcharge = useAppSelector(
    projectSelectors.bomItemMaterialFinancialSelector(
      bomItemPointer,
      purchasingActivity?.id,
      'surchargeRatio',
    ),
    isEqual
  ) as QuantityDto

  const currentDefinedDiscount = useAppSelector(
    projectSelectors.bomItemMaterialFinancialSelector(
      bomItemPointer,
      purchasingActivity?.id,
      'discountRatio',
    ),
    isEqual
  ) as QuantityDto

  const currentMaterialSummary = useAppSelector(
    projectSelectors.bomItemActivityArticleSelector(
      bomItemPointer,
      purchasingActivity?.id,
    ),
    isEqual
  )

  const partTypeIsPurchasing = useAppSelector(
    projectSelectors.bomItemIsPurchasingPartSelector(bomItemPointer),
    isEqual
  )

  const [hasChanges, setHasChanges] = useState<boolean>(false)

  const { controller, loading } = useAppController(
    () => new PurchasingInfoController(),
  )

  const selectedArticle: MaterialSummaryDto = useAppSelector((state) => {
    return (
      state.materials.articleSelectorUI?.selectedMaterial ??
      purchasingActivity?.article
    )
  }, isEqual)

  useEffect(() => {
    return () => {
      dispatch(MaterialActions.closeModal())
    }
  }, [dispatch, bomItemPointer])

  const [useArticleDefinedPrice, setUseArticleDefinedPrice] = useState(() => {
    return Boolean(currentMaterialSummary?.id)
  })

  useEffect(() => {
    if (selectedArticle?.id !== currentMaterialSummary?.id) {
      setHasChanges(true)
    }
  }, [selectedArticle?.id, currentMaterialSummary?.id])

  const currentlyIsCustomPrice = useAppSelector(
    projectSelectors.bomItemMaterialHasCustomPrice(
      bomItemPointer,
      purchasingActivity?.id,
    ),
  )

  const [customPrice, setCustomPrice] = useState<CustomPrice | undefined>({
    CostPrice: currentDefinedCostPrice,
    SurchargeRatio: currentDefinedSurcharge,
    DiscountRatio: currentDefinedDiscount,
  })

  const [isPurchasingPart, setIsPurchasingPart] = useState(true)

  const surchargeFieldRef = useRef<BomItemMaterialSurchargePercentageRef>(null)
  const discountFieldRef = useRef<BomItemMaterialDiscountPercentageRef>(null)

  const resetCustomPrice = useCallback(() => {
    setCustomPrice({
      CostPrice: currentDefinedCostPrice,
      SurchargeRatio: currentDefinedSurcharge,
      DiscountRatio: currentDefinedDiscount,
    })
    surchargeFieldRef.current?.reset()
    discountFieldRef.current?.reset()

    setHasChanges(false)

    if (currentMaterialSummary?.id) {
      setUseArticleDefinedPrice(true)
    } else {
      setUseArticleDefinedPrice(false)
    }
  }, [
    currentDefinedCostPrice,
    currentDefinedDiscount,
    currentDefinedSurcharge,
    currentMaterialSummary?.id,
  ])

  useEffect(() => {
    resetCustomPrice()
  }, [resetCustomPrice])

  const storeCustomPrice = useCallback(
    (key: keyof CustomPrice, value: MoneyDto | QuantityDto) => {
      setCustomPrice((old) => {
        const newCustomPrice = {
          ...(old || {
            CostPrice: undefined,
            DiscountRatio: undefined,
            SurchargeRatio: undefined,
          }),
          [key]: value,
        }

        return newCustomPrice
      })
      setHasChanges(true)
    },
    [],
  )

  const handleClose = useCallback(() => {
    resetCustomPrice()
    dispatch(
      MaterialActions.initMaterialSelector(
        [bomItemPointer],
        WorkingStepType.Purchasing,
      ),
    )
  }, [bomItemPointer, dispatch, resetCustomPrice])

  const handleSave = useCallback(async () => {
    try {
      if (!hasChanges) {
        handleClose()
        return
      }

      if (isPurchasingPart) {
        let keywordsToSend = Object.values(
          selectedArticle?.tokens || {},
        ).flatMap((x: Array<KeywordDto> | KeywordDto) => {
          if (Array.isArray(x)) {
            return x.map((x) => x.originalKeyword)
          } else {
            return x.originalKeyword
          }
        })

        if (keywordsToSend[0] !== 'part') {
          keywordsToSend = ['part']
        }

        await controller.setBomItemAsPurchasingPart(bomItemPointer.id, {
          articleId: useArticleDefinedPrice ? selectedArticle?.id : undefined,
          keywords: useArticleDefinedPrice ? keywordsToSend : ['part'],
          costPricePerItem: !useArticleDefinedPrice
            ? customPrice?.CostPrice
            : undefined,
          surchargePercentage: !useArticleDefinedPrice
            ? customPrice?.SurchargeRatio?.value || 0
            : undefined,
          discountPercentage: !useArticleDefinedPrice
            ? customPrice?.DiscountRatio?.value || 0
            : undefined,
        })
      } else {
        await controller.unsetAsPurchasingPart(bomItemPointer.id)
        handleClose()
      }

      setHasChanges(false)
    } catch (err) {
      ShowException('purchasing data', err)
    }
  }, [
    bomItemPointer.id,
    controller,
    customPrice?.CostPrice,
    customPrice?.DiscountRatio?.value,
    customPrice?.SurchargeRatio?.value,
    handleClose,
    hasChanges,
    isPurchasingPart,
    selectedArticle?.id,
    selectedArticle?.tokens,
    useArticleDefinedPrice,
  ])

  const handleChangeIsPurchasingPart = useCallback((checked: boolean) => {
    setIsPurchasingPart(checked)
    if (checked === false) {
      setUseArticleDefinedPrice(false)
    }
    setHasChanges(true)
  }, [])

  const handleChangeUseArticleDefinedPrice = useCallback(
    (checked: boolean) => {
      startTransition(() => {
        setUseArticleDefinedPrice(checked)

        if (checked === true) {
          setIsPurchasingPart(true)
        } else {
          dispatch(materialActions.closeWithoutSave())
        }

        if (!currentMaterialSummary?.id && checked === false && !hasChanges) {
          // if there are no changes and the original state is without article, do not mark as changed
          setHasChanges(false)
        } else if (
          checked === true &&
          currentMaterialSummary?.id === selectedArticle?.id
        ) {
          // if there the selectedArticle is the same as the original one, do not mark as changed
          setHasChanges(false)
        } else {
          // otherwise, there are changes
          setHasChanges(true)
        }
      })
    },
    [dispatch, currentMaterialSummary?.id, selectedArticle?.id, hasChanges],
  )

  const handleUnsetAsPurchasingPart = useCallback(() => {
    dispatch(saveBomItemIsPurchasingPart({ bomItemPointer }))
  }, [bomItemPointer, dispatch])

  return {
    partTypeIsPurchasing,
    selectedArticle,
    useArticleDefinedPrice,
    handleChangeUseArticleDefinedPrice,
    customPrice,
    isPurchasingPart,
    handleChangeIsPurchasingPart,
    handleClose,
    handleSave,
    loading,
    currentlyIsCustomPrice,
    storeCustomPrice,
    hasChanges,
    purchasingActivityId: purchasingActivity?.id,
    isValid: Boolean(
      !useArticleDefinedPrice ||
      (useArticleDefinedPrice && selectedArticle?.id),
    ),
    resetCustomPrice,
    surchargeFieldRef,
    discountFieldRef,
    handleUnsetAsPurchasingPart,
    projectIsEditable,
  }
}
